/* RESPONSIBLE TEAM: team-app-security */
import Model, { attr, belongsTo } from '@ember-data/model';
import IpAllowListValidator from '../lib/ip-allowlist-validator';
import { inject as service } from '@ember/service';

export default class SecurityAppSettings extends Model {
  @service appService;
  @service intl;
  @service intercomEventService;

  // Staging property for redaction rules. The component writes here and the
  // serializer reads from here, bypassing Ember Data's unreliable @attr array
  // dirty-tracking entirely.
  _pendingRedactionRules = null;

  @attr('boolean') force_2fa;
  @attr('array') authorizedSignInMethods;
  @attr('array') ssoDomainWhitelist;
  @belongsTo('saml-account', { async: false }) samlAccount;
  @belongsTo('scim-setting', { async: false }) scimSettings;
  @attr('boolean') canEnableEnforceSso;
  @attr('boolean') canEnforceSaml;
  @attr('boolean') can_enforce_2fa;
  @attr('boolean') endUserUploadsAccessDisabled;
  @attr('boolean') notificationEmailsWithContentEnabled;
  @attr('boolean') leadEmailIdentificationEnabled;
  @attr('string') securityContactEmail;
  @attr('string') samlBaseUrl;
  @attr('boolean') unauthenticatedLeadMergesEnabled;
  @attr('boolean') mergeConversationsCrossUserEnabled;
  @attr('boolean') canToggleNotificationEmailsWithContent;
  @attr('boolean') conversationPanRedactionEnabled;
  @attr('boolean') piiAttributeMaskingEnabled;
  @attr('boolean') conversationRedactionEnabled;
  @attr('array') conversationRedactionRules;
  @attr('boolean') ipAllowlistEnabled;
  @attr('array') ipAllowlist;
  @attr('boolean') restApiIpAllowlistEnabled;
  @attr('array') restApiIpAllowlist;
  @attr('boolean') customSessionLengthEnabled;
  @attr('number') customSessionLength;
  @attr('array') customSessionLengthValidExpiries;
  @belongsTo('conversation-deletion-schedule', { async: false }) conversationDeletionSchedule;

  async save() {
    this.beforeSave();
    if (this._pendingRedactionRules !== null) {
      // Pass pending redaction rules via adapterOptions so the serializer can
      // include them in the PUT payload. snapshot.adapterOptions is a first-class
      // Ember Data API, unlike snapshot.record which may not preserve plain
      // (non-@attr) properties reliably.
      let rules = this._pendingRedactionRules;
      this._pendingRedactionRules = null;
      await super.save({ adapterOptions: { pendingRedactionRules: rules } });
    } else {
      await super.save(...arguments);
    }
  }

  beforeSave() {
    if (!this.isInvalid) {
      if (this.ipAllowlistEnabled) {
        this.updateIpAllowlist();
      }
      if (this.restApiIpAllowlistEnabled) {
        this.updateRestApiIpAllowlist();
      }
    }
  }

  get analyticsData() {
    return {
      object: 'security_app_settings',
      security_app_settings_id: this.id,
    };
  }

  get isInvalid() {
    return !this.ipValidator.isValid || !this.restApiIpValidator.isValid;
  }

  get ipValidator() {
    let ipAllowlist = this.ipAllowlist;
    let ipString = this._isArray(ipAllowlist) ? ipAllowlist.join(',') : ipAllowlist;
    return new IpAllowListValidator(ipString, this.intl);
  }

  _isArray(list) {
    return Array.isArray(list);
  }

  updateIpAllowlist() {
    if (this._isArray(this.ipAllowlist)) {
      return;
    }

    let ipAllowlist = this.ipAllowlist;
    this.ipAllowlist = ipAllowlist.split(',').map((ip) => ip.trim());
  }

  get restApiIpValidator() {
    let restApiIpAllowlist = this.restApiIpAllowlist;
    let ipString = this._isArray(restApiIpAllowlist)
      ? restApiIpAllowlist.join(',')
      : restApiIpAllowlist;
    return new IpAllowListValidator(ipString, this.intl);
  }

  updateRestApiIpAllowlist() {
    if (this._isArray(this.restApiIpAllowlist)) {
      return;
    }

    let restApiIpAllowlist = this.restApiIpAllowlist;
    this.restApiIpAllowlist = restApiIpAllowlist.split(',').map((ip) => ip.trim());
  }
}
